import React, { useState, useEffect } from "react"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listWebhookEndpoints, createWebhookEndpoint, deleteWebhookEndpoint } from "../../../utils/webhookHelpers"

const EVENT_OPTIONS = [
  { key: "course.completed", label: "Course Completed" },
  { key: "credential.issued", label: "Credential Issued" },
]

const EndpointFormModal = ({ orgId, onClose, onCreated }) => {
  const [url, setUrl] = useState("")
  const [selectedEvents, setSelectedEvents] = useState([])
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState("")

  const toggleEvent = (key) => {
    setSelectedEvents((prev) => (prev?.includes(key) ? prev?.filter((e) => e !== key) : [...prev, key]))
  }

  const handleCreate = async (e) => {
    e?.preventDefault()
    setError("")
    if (!url?.trim() || !/^https?:\/\//.test(url.trim())) {
      setError("Enter a valid URL starting with http:// or https://")
      return
    }
    if (selectedEvents?.length === 0) {
      setError("Select at least one event to subscribe to")
      return
    }
    setIsCreating(true)
    const { error: createError } = await createWebhookEndpoint({ orgId, url, events: selectedEvents })
    setIsCreating(false)
    if (createError) {
      setError(createError?.message || "Could not create endpoint")
      return
    }
    onCreated()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="max-w-md w-full dark:bg-card">
        <div className="flex items-center justify-between px-5">
          <h3 className="text-lg font-semibold text-foreground">New Webhook Endpoint</h3>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={18} />
        </div>
        <CardContent>
          <form onSubmit={handleCreate} className="space-y-4">
            <Input
              label="Endpoint URL"
              placeholder="https://your-server.com/webhooks/nesolearn"
              value={url}
              onChange={(e) => setUrl(e?.target?.value)}
              required
            />
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Events</label>
              <div className="space-y-2 border border-border rounded-md p-3">
                {EVENT_OPTIONS?.map((option) => (
                  <label key={option?.key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedEvents?.includes(option?.key)}
                      onChange={() => toggleEvent(option?.key)}
                      className="rounded border-border"
                    />
                    <span className="text-foreground">{option?.label}</span>
                    <code className="text-xs text-muted-foreground">({option?.key})</code>
                  </label>
                ))}
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" fullWidth onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="default" fullWidth loading={isCreating}>
                Create Endpoint
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const WebhooksPanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [endpoints, setEndpoints] = useState([])
  const [loading, setLoading] = useState(true)
  const [showFormModal, setShowFormModal] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const loadEndpoints = async () => {
    setLoading(true)
    const { endpoints: list } = await listWebhookEndpoints(orgId)
    setEndpoints(list || [])
    setLoading(false)
  }

  useEffect(() => {
    if (orgId) loadEndpoints()
  }, [orgId])

  const handleCreated = () => {
    setShowFormModal(false)
    loadEndpoints()
  }

  const handleDelete = (endpointId) => {
    if (!window.confirm("Delete this webhook endpoint? This cannot be undone.")) return
    setPendingDeleteId(endpointId)
  }

  const confirmDelete = async () => {
    await deleteWebhookEndpoint(orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadEndpoints()
  }

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {endpoints?.length || 0} endpoint{endpoints?.length === 1 ? "" : "s"}
        </p>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowFormModal(true)}>
          New Endpoint
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-sm">Loading endpoints...</div>
      ) : endpoints?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Webhook" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No webhook endpoints yet</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Register a URL to receive real-time POST requests when a course is completed or a credential is issued.
          </p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowFormModal(true)}>
            New Endpoint
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {endpoints?.map((endpoint) => (
            <Card key={endpoint?.endpointId} className="dark:bg-transparent">
              <CardContent className="pt-4 pb-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-mono text-foreground truncate">{endpoint?.url}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    {endpoint?.events?.map((eventKey) => (
                      <Badge key={eventKey} variant="outline">
                        {EVENT_OPTIONS?.find((o) => o?.key === eventKey)?.label || eventKey}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(endpoint?.endpointId)} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showFormModal && (
        <EndpointFormModal orgId={orgId} onClose={() => setShowFormModal(false)} onCreated={handleCreated} />
      )}

      <ReauthModal open={!!pendingDeleteId} label="delete this webhook endpoint" onConfirm={confirmDelete} onClose={() => setPendingDeleteId(null)} />
    </div>
  )
}

export default WebhooksPanel